/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WarehouseInsightsPage.tsx
 *     Feature         :   217-insights-dashboards (T020 / US3, FR-003)
 *     Description     :   Warehouse Insights — fleet health: size, growth, protection,
 *                         row counts, and refresh activity. Every list carries workspace +
 *                         capacity attribution. Everything renders from collected data
 *                         (FR-014); unmeasured warehouses and missing row counts show a
 *                         real reason via NotCollected, never a fabricated 0.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { Database, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, StatCard, AppTooltip, ErrorState, LoadingState, NotCollected,
  Extended, QuickGlance, chartTooltipStyle,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEasternDate } from '../../utils/dateFormatter';
import { AccessGate } from '../../components/access/AccessGate';

interface Attribution {
  workspaceName: string | null;
  capacityName: string | null;
}

interface TopByStorageRow extends Attribution { warehouseId: string; name: string; storageGb: number }
interface UnmeasuredRow extends Attribution { warehouseId: string; name: string; reason: string | null }
interface GrowthRow extends Attribution { warehouseId: string; name: string; earliestGb: number; latestGb: number; growthPct: number | null }
interface RowsRefreshRow extends Attribution { warehouseId: string; name: string; rowCount: number | null; changeEvents60d: number; lastChangedAt: string | null }

interface WarehouseInsightsPayload {
  kpis: {
    totalWarehouses: number;
    measuredCount: number;
    measuredCoveragePct: number | null;   // null over no warehouses (Spec 298 FR-004)
    totalStorageGb: number;
    /** Spec 285: withheld by the API (absent) without the backup license. */
    backupProtectedCount?: number;
    /** Absent when withheld (unlicensed); null over no warehouses (Spec 298 FR-004). */
    backupProtectedPct?: number | null;
  };
  fleetStorageTrend: Array<{ date: string; storageGb: number }>;
  tableCountTrend: Array<{ date: string; tableCount: number }>;
  topByStorage: TopByStorageRow[];
  unmeasured: UnmeasuredRow[];
  growthRanking: GrowthRow[];
  rowsAndRefresh: RowsRefreshRow[];
}

/** Warehouse name cell (links to the warehouse detail) with a workspace · capacity subtitle. */
function nameCell<T extends { name: string; warehouseId: string } & Attribution>(r: T): React.ReactNode {
  return (
    <div className="min-w-0">
      <Link
        to={`/warehouses/${r.warehouseId}`}
        className="truncate block font-medium text-[var(--color-accent)] hover:underline"
      >
        {r.name}
      </Link>
      <div className="truncate text-xs text-[var(--color-text-secondary)]">
        {r.workspaceName ?? 'no workspace'}{r.capacityName ? ` · ${r.capacityName}` : ''}
      </div>
    </div>
  );
}

export const WarehouseInsightsPage: React.FC = () => {
  const query = useQuery<WarehouseInsightsPayload>({
    queryKey: ['warehouse-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: WarehouseInsightsPayload }>('/api/warehouse-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  const topByStorageColumns: DataTableColumn<TopByStorageRow>[] = [
    { key: 'name', header: 'Warehouse', width: '62%', render: nameCell },
    { key: 'storageGb', header: 'Storage (GB)', width: '38%', align: 'right', render: (r) => r.storageGb.toLocaleString() },
  ];

  const growthColumns: DataTableColumn<GrowthRow>[] = [
    { key: 'name', header: 'Warehouse', width: '40%', render: nameCell },
    { key: 'earliestGb', header: '60d ago (GB)', width: '18%', align: 'right' },
    { key: 'latestGb', header: 'Now (GB)', width: '18%', align: 'right' },
    {
      key: 'growthPct', header: 'Growth', width: '24%', align: 'right',
      render: (r) => (r.growthPct == null ? '—' : <span className={r.growthPct > 0 ? 'text-[var(--color-status-warning)]' : undefined}>{r.growthPct > 0 ? '+' : ''}{r.growthPct}%</span>),
    },
  ];

  const rowsColumns: DataTableColumn<RowsRefreshRow>[] = [
    { key: 'name', header: 'Warehouse', width: '40%', render: nameCell },
    {
      key: 'rowCount', header: 'Total rows', width: '22%', align: 'right',
      render: (r) => (r.rowCount == null ? <NotCollected reason="No completed backup with row counts yet" /> : r.rowCount.toLocaleString()),
    },
    {
      key: 'changeEvents60d', header: 'Data changes (60d)', width: '20%', align: 'right',
      render: (r) => (r.changeEvents60d > 0 ? r.changeEvents60d : <span className="text-[var(--color-text-secondary)]">0</span>),
    },
    {
      key: 'lastChangedAt', header: 'Last change', width: '18%', align: 'right',
      render: (r) => (r.lastChangedAt ? formatEasternDate(r.lastChangedAt) : <span className="text-[var(--color-text-secondary)]">—</span>),
    },
  ];

  const unmeasuredColumns: DataTableColumn<UnmeasuredRow>[] = [
    { key: 'name', header: 'Warehouse', width: '50%', render: nameCell },
    { key: 'reason', header: 'Reason', width: '50%', render: (r) => <NotCollected reason={r.reason ?? undefined} /> },
  ];

  useAutoRefresh(refresh, { lastFetchedAt });

  return (
    <PageLayout
      title="Warehouse Insights"
      description="Warehouse fleet health — size, growth, protection, row counts, and refresh activity"
      icon={<Database className="h-6 w-6" />}
      helpId="insights.warehouse"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load warehouse insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Warehouses" value={data.kpis.totalWarehouses} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="All active warehouses in the tenant (dbo.warehouses, is_active) — the denominator for measured coverage and protection below.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard
                className="h-full"
                label="Measured Coverage"
                value={data.kpis.measuredCoveragePct === null ? 'no warehouses' : `${data.kpis.measuredCoveragePct}%`}
                valueClassName={data.kpis.measuredCoveragePct !== null && data.kpis.measuredCoveragePct < 90 ? 'text-[var(--color-status-warning)]' : undefined}
              />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Warehouses whose size was successfully measured ÷ total, from OneLake byte-counting — measured means size_measurement_error IS NULL (Spec 238); unmeasured ones carry a reason instead of a silent zero.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Storage (GB)" value={data.kpis.totalStorageGb.toLocaleString()} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Sum of measured warehouse sizes in GB (dbo.warehouses.storage_size_gb), counting only warehouses with a successful measurement — unmeasured ones are excluded, never zeroed.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            {/* Spec 285: backup figures — backup-licensed only (the API withholds them otherwise). */}
            <AccessGate area="backup">
            <div className="relative h-full">
              <StatCard className="h-full" label="Backup-Protected" value={data.kpis.backupProtectedCount ?? '—'} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Warehouses covered by a backup plan — the warehouse itself or its workspace has a dbo.backup_plan_targets row.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Protected %" value={data.kpis.backupProtectedPct === undefined ? '—' : data.kpis.backupProtectedPct === null ? 'no warehouses' : `${data.kpis.backupProtectedPct}%`} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Backup-Protected ÷ Total Warehouses — share of the active fleet covered by a backup plan.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            </AccessGate>
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for trends, growth, row counts, and refresh activity.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Fleet storage — 60 days</h2>
                    <AppTooltip title="Total warehouse storage as of each day, carried forward per warehouse — a missing-snapshot day holds the last known size instead of dipping to zero.">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  {data.fleetStorageTrend.length > 1 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.fleetStorageTrend}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={40} />
                          <Tooltip {...chartTooltipStyle} />
                          <Line type="monotone" dataKey="storageGb" name="Storage (GB)" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <NotCollected reason="Fewer than 2 days of warehouse snapshots collected in the 60-day window" />
                  )}
                </Card>

                <Card>
                  <h2 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">Table count — 60 days</h2>
                  {data.tableCountTrend.length > 1 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.tableCountTrend}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={40} />
                          <Tooltip {...chartTooltipStyle} />
                          <Line type="monotone" dataKey="tableCount" name="Tables" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <NotCollected reason="Fewer than 2 days of warehouse snapshots collected in the 60-day window" />
                  )}
                </Card>
              </div>

              <Card padding="none">
                <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Top by storage</h2>
                <DataTable columns={topByStorageColumns} rows={data.topByStorage} rowKey={(r) => r.warehouseId} pageSize={10} emptyMessage="No measured warehouses" />
              </Card>

              <Card padding="none">
                <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Growth ranking — 60 days</h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Which warehouse is growing fastest (top row = fastest)</p>
                <DataTable columns={growthColumns} rows={data.growthRanking} rowKey={(r) => r.warehouseId} pageSize={10} emptyMessage="Not enough history to rank growth" />
              </Card>

              <Card padding="none">
                <div className="flex items-center gap-2 px-4 pt-4">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Row counts &amp; refresh activity</h2>
                  <AppTooltip title="Total rows come from the latest completed backup of each warehouse. 'Data changes' counts how many times the warehouse's size or table count changed across snapshots in the last 60 days — a proxy for how often it's refreshed, since Fabric does not expose per-warehouse query telemetry.">
                    <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                  </AppTooltip>
                </div>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Most rows first · data changes = refresh proxy (query telemetry not collected)</p>
                <DataTable columns={rowsColumns} rows={data.rowsAndRefresh} rowKey={(r) => r.warehouseId} pageSize={10} emptyMessage="No warehouses" />
              </Card>

              <Card padding="none">
                <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Unmeasured warehouses</h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Every gap carries a reason — never a silent zero</p>
                <DataTable columns={unmeasuredColumns} rows={data.unmeasured} rowKey={(r) => r.warehouseId} pageSize={10} emptyMessage="All warehouses measured" />
              </Card>
            </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default WarehouseInsightsPage;
